import { useEffect, useRef, useState, type ChangeEvent } from "react";
import { ART_DIRECTION_NONE, mvArtDirections, type Cut, type GenerationErrorCode } from "@studio/shared";
import { InlineSpinner } from "../components/InlineSpinner";
import { StyleSampleDialog } from "../components/StyleSampleDialog";
import { useBackClosable } from "@teachermate/shared/back";
import { CharCounter, ErrorPage, InlineError, LoadingPage, StudioShell } from "../components/StudioShell";
import { ApiError, apiRequest, useJobProgress, useWork, waitForJob } from "../lib/api";
import { navigate } from "../lib/navigation";
import { pathForStep } from "../lib/session";


const imageFailureMessages: Record<GenerationErrorCode, string> = {
  PROVIDER_TIMEOUT: "시간이 오래 걸려 멈췄어요. 다시 시도해 주세요.",
  PROVIDER_REJECTED: "그림을 만들지 못했어요. 장면을 조금 바꿔 보세요.",
  MODERATION_BLOCKED: "안전한 장면으로 고쳐서 다시 시도해 주세요.",
  BUDGET_EXHAUSTED: "오늘 만들 수 있는 그림을 다 썼어요.",
  INTERNAL: "그림을 만들지 못했어요. 다시 시도해 주세요.",
};

export function CutsPage({ workId }: { workId: string }) {
  const query = useWork(workId);
  const [artDirection, setArtDirection] = useState<string | null>(null);
  /** 분위기 예시 창에 띄운 분위기(2026-10-20 jery) — 「이 느낌으로 할게요」를 눌러야 고른다. */
  const [sampleKey, setSampleKey] = useState<(typeof mvArtDirections)[number]["key"] | null>(null);
  /** 「초기화」 확인 창(2026-10-20 jery · 나안) — 누르면 바로 지우지 않고 한 번 묻는다. */
  const [resetAsk, setResetAsk] = useState(false);
  const [imageUrls, setImageUrls] = useState<Record<string, string>>({});
  const [localErrors, setLocalErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<Record<string, boolean>>({});
  const [pageError, setPageError] = useState<string | null>(null);
  /** 「나머지 컷 만들기」가 몇 번째를 만들고 있는지 — 순차라 오래 걸려서 필요하다. */
  const [batchProgress, setBatchProgress] = useState<{ done: number; total: number } | null>(null);
  /** 기준 컷에 그림을 새로 올린 직후 — 이미 그려 둔 뒤 컷들을 새 기준으로 다시 그릴지 묻는다.
      🚨 묻지 않고 자동으로 돌리지 않는 이유: 다시 만들기 횟수를 컷마다 1번씩 «소모» 하기로
      했으므로(2026-08-20 jery), 학생 몫의 횟수를 쓰는 결정은 학생 눈앞에서 일어나야 한다. */
  const [cascade, setCascade] = useState<{ stage: "offer"; count: number } | { stage: "running" } | null>(null);

  useEffect(() => {
    if (query.data?.work.art_direction && artDirection === null) {
      setArtDirection(query.data.work.art_direction);
    }
  }, [artDirection, query.data?.work.art_direction]);

  useEffect(() => {
    const cuts = query.data?.cuts ?? [];
    let cancelled = false;
    void Promise.all(cuts.flatMap((cut) => cut.image_asset_id
      ? [apiRequest<{ url: string }>(`/api/assets/${encodeURIComponent(cut.image_asset_id)}/url`)
          .then((result) => [cut.id, result.url] as const)
          .catch(() => null)]
      : []
    )).then((pairs) => {
      if (cancelled) return;
      setImageUrls(Object.fromEntries(pairs.filter((pair): pair is readonly [string, string] => pair !== null)));
    });
    return () => { cancelled = true; };
  }, [query.data?.cuts]);

  if (query.loading) return <LoadingPage label="컷 만들기 화면을 불러오는 중..." />;
  if (!query.data || query.error) {
    return <ErrorPage message={query.error?.message ?? "컷 만들기 화면을 불러오지 못했습니다."} />;
  }
  const data = query.data;
  const base = data.cuts.find((cut) => cut.idx === 1);
  const artLocked = Boolean(base?.image_asset_id || base?.current_generation_job_id);

  const withBusy = async (key: string, action: () => Promise<void>): Promise<void> => {
    setBusy((current) => ({ ...current, [key]: true }));
    setLocalErrors((current) => ({ ...current, [key]: "" }));
    try {
      await action();
    } catch (caught) {
      const message = caught instanceof ApiError
        ? caught.code === "LIMIT_EXHAUSTED"
          ? "그림 다시 만들기 횟수를 다 썼어요. 선생님께 요청해 주세요."
          : caught.message
        : "요청을 처리하지 못했어요.";
      setLocalErrors((current) => ({ ...current, [key]: message }));
    } finally {
      setBusy((current) => ({ ...current, [key]: false }));
    }
  };

  const madeCount = data.cuts.filter((cut) => cut.image_asset_id).length;
  const anyGenerating = data.cuts.some((cut) => cut.current_generation_job_id);
  const canReset = madeCount > 0 || artLocked || artDirection !== null;
  const resetCuts = async (): Promise<void> => {
    await withBusy("reset", async () => {
      await apiRequest(`/api/works/${encodeURIComponent(workId)}/cuts/reset`, { method: "POST" });
      // 🩸 새 데이터를 «먼저» 받는다. 분위기를 먼저 비우면 위 useEffect 가 아직 남은 옛 데이터의
      //    분위기로 다시 채워서, 서버는 비었는데 화면은 옛 분위기를 고른 채로 남았다(2026-10-20 로컬 실측).
      await query.refresh();
      setArtDirection(null);
      setImageUrls({});
      setCascade(null);
      setResetAsk(false);
    });
  };

  const saveArtDirection = async (): Promise<void> => {
    if (!artDirection) throw new ApiError(422, "INVALID", "그림 분위기를 먼저 골라 주세요.");
    await apiRequest(`/api/works/${encodeURIComponent(workId)}/autosave`, {
      method: "POST",
      body: JSON.stringify({ step: "cuts", art_direction: artDirection, gifts_v: 2 }),
    });
  };

  const startImage = async (
    cut: Cut,
    mode: "generate" | "edit",
    instruction?: string,
  ): Promise<string | null> => {
    let jobId: string | null = null;
    await withBusy(`image:${cut.id}`, async () => {
      await saveArtDirection();
      const started = await apiRequest<{ job_id: string }>(
        `/api/cuts/${encodeURIComponent(cut.id)}/image`,
        {
          method: "POST",
          headers: { "Idempotency-Key": crypto.randomUUID() },
          body: JSON.stringify({ mode, ...(instruction ? { instruction } : {}) }),
        },
      );
      jobId = started.job_id;
      await query.refresh();
    });
    return jobId;
  };

  const uploadImage = async (cut: Cut, event: ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    await withBusy(`upload:${cut.id}`, async () => {
      const form = new FormData();
      form.set("image", file);
      await apiRequest(`/api/cuts/${encodeURIComponent(cut.id)}/upload-image`, {
        method: "POST",
        body: form,
      });
      // 기준 컷의 그림이 바뀌었다 — 이미 그려 둔 뒤 컷들은 옛 기준을 문 채라 다시 그릴지 묻는다.
      // (뒤 컷 재생성은 기준 컷의 최신 그림을 참고로 받으므로, 서버 쪽은 이미 새 기준을 따른다.)
      if (cut.is_base) {
        const affected = data.cuts.filter((other) => other.idx > 1 && other.image_asset_id).length;
        if (affected > 0) setCascade({ stage: "offer", count: affected });
      }
      await query.refresh();
    });
  };

  const confirmBase = async (): Promise<void> => {
    await withBusy("confirm-base", async () => {
      await apiRequest(`/api/works/${encodeURIComponent(workId)}/confirm-base`, {
        method: "POST",
        body: JSON.stringify({}),
      });
      await query.refresh();
    });
  };

  const generateRemaining = async (): Promise<void> => {
    const pending = data.cuts.filter((cut) => cut.idx > 1 && !cut.image_asset_id && !cut.current_generation_job_id);
    // 🚨 종전엔 `Promise.all` 로 **남은 컷을 한꺼번에** 걸었다. 그러면 한 학생의 컷 대여섯 장이
    //    동시에 만들어지면서 화면이 여러 군데서 함께 돌고, 무엇이 언제 끝나는지 읽히지 않는다
    //    (2026-08-13 jery: 「전부 순차, 1장씩」).
    // 🔑 «앞 컷이 끝난 뒤 다음 컷» 이라 학생 한 명 안에서만 줄을 세운다 —
    //    학생끼리는 여전히 동시에 진행된다(서버 워커 동시 실행 수는 건드리지 않았다).
    //    한 반 25명의 수업 시간이 늘어나지 않는 이유가 이것이다.
    setBusy((current) => ({ ...current, "generate-all": true }));
    setBatchProgress({ done: 0, total: pending.length });
    try {
      for (const [index, cut] of pending.entries()) {
        setBatchProgress({ done: index, total: pending.length });
        const jobId = await startImage(cut, "generate");
        // 시작조차 못 했으면(한도 초과·오류) 줄을 세울 대상이 없다. 오류는 그 컷 자리에 이미 떴다.
        if (!jobId) continue;
        await waitForJob(jobId);
        await query.refresh();
      }
    } finally {
      setBusy((current) => ({ ...current, "generate-all": false }));
      setBatchProgress(null);
    }
  };

  /** 기준 컷 업로드 뒤, 이미 그려 둔 뒤 컷들을 새 기준으로 한 장씩 다시 그린다.
      「나머지 컷 만들기」와 같은 순차 규칙(migration 016: 한 학생 안에서 그림은 한 장씩)이고,
      다시 만들기 횟수를 컷마다 1번씩 쓴다 — 한도가 먼저 바닥나면 그 컷 자리에 오류가 뜨고
      다음 컷으로 넘어간다(멈추면 앞쪽만 새 기준이 되고 뒤쪽은 조용히 남는다). */
  const regenerateFromNewBase = async (): Promise<void> => {
    setCascade({ stage: "running" });
    const targets = data.cuts.filter((cut) => cut.idx > 1 && cut.image_asset_id && !cut.current_generation_job_id);
    setBusy((current) => ({ ...current, "generate-all": true }));
    setBatchProgress({ done: 0, total: targets.length });
    try {
      for (const [index, cut] of targets.entries()) {
        setBatchProgress({ done: index, total: targets.length });
        const jobId = await startImage(cut, "generate");
        if (!jobId) continue;
        await waitForJob(jobId);
        await query.refresh();
      }
    } finally {
      setBusy((current) => ({ ...current, "generate-all": false }));
      setBatchProgress(null);
      setCascade(null);
    }
  };

  const finish = async (): Promise<void> => {
    setPageError(null);
    try {
      await apiRequest(`/api/works/${encodeURIComponent(workId)}/autosave`, {
        method: "POST",
        body: JSON.stringify({ step: "mv_done", gifts_v: 2 }),
      });
      navigate(pathForStep(workId, "mv_done"));
    } catch (caught) {
      setPageError(caught instanceof ApiError ? caught.message : "완료 상태를 저장하지 못했어요.");
    }
  };

  const allComplete = data.cuts.length > 0 && data.cuts.every((cut) => cut.image_asset_id);
  // 🚨 컷 그림은 «한 학생 안에서 한 장씩» 이다(2026-08-14 jery 5번). 종전엔 「나머지 컷 만들기」만
  //    순차였고, 컷 카드를 하나씩 눌러 가는 길은 열려 있어 서너 장이 동시에 돌았다.
  //    서버 쪽 정본 = migration 016(IMAGE_ACTIVE). 여기서는 «누르기 전에» 막아, 학생이
  //    거절당하는 대신 「지금은 차례가 아니다」를 읽게 한다.
  // 🔑 서버가 이미 job 을 들고 있는 컷(current_generation_job_id) + 아직 응답을 기다리는 컷
  //    (busy["image:*"]) 둘 다 본다 — 후자를 빼면 연타로 두 장이 시작된다.
  const generatingCutId = data.cuts.find((cut) => cut.current_generation_job_id)?.id
    ?? Object.keys(busy).find((key) => key.startsWith("image:") && busy[key])?.slice("image:".length)
    ?? null;
  return (
    <StudioShell currentStep={3} imageRegenRemaining={data.image_regen_remaining}>
      <div className="mv-page cuts-page">
        <header className="mv-heading">
          <span className="mv-kicker">MV 3 · 컷 만들기</span>
          <h1 tabIndex={-1}>장면마다 그림을 만들어요</h1>
          <p>첫 장면의 느낌을 정한 뒤 같은 분위기로 나머지 컷을 이어 가요.</p>
          {/*
            🔑 업로드 규격은 **여기 한 번만** 적는다 — 컷 카드는 컷 수만큼 반복되므로
               카드마다 넣으면 같은 문장이 화면을 도배한다. 버튼 쪽은 aria-label 로 잇는다.
               숫자 출처 = cuts.ts (detectImageMime = PNG·JPEG·WEBP / maxImageBytes = 10MB).
          */}
          <p className="mv-upload-formats">내 그림 업로드는 <strong>PNG·JPG·WEBP, 10MB 이하</strong>까지 올릴 수 있어요.</p>
        </header>
        <section className="mv-card art-direction-card">
          <div>
            <h2>그림 분위기</h2>
            <p>{artLocked
              ? "기준 컷을 만들기 시작해서 분위기가 잠겼어요."
              : "누르면 예시 그림이 나와요. 기준 컷을 만든 뒤에는 분위기를 바꿀 수 없어요."}</p>
          </div>
          <div className="art-chip-grid">
            {mvArtDirections.map((option) => (
              <button
                type="button"
                className={artDirection === option.label ? "selected" : ""}
                aria-pressed={artDirection === option.label}
                disabled={artLocked}
                key={option.key}
                onClick={() => { if (artDirection !== option.label) setSampleKey(option.key); }}
              >{option.label}</button>
            ))}
            {/* 「선택 안 함」 = 분위기 문장 없이 내가 쓴 장면만으로 그린다(서버 mvArtDirectionLine). 예시가 없어 창 없이 바로 고른다. */}
            <button
              type="button"
              className={artDirection === ART_DIRECTION_NONE ? "selected" : ""}
              aria-pressed={artDirection === ART_DIRECTION_NONE}
              disabled={artLocked}
              onClick={() => setArtDirection(ART_DIRECTION_NONE)}
            >{ART_DIRECTION_NONE}</button>
          </div>
          {artDirection === ART_DIRECTION_NONE ? (
            <p className="art-none-note">분위기를 정하지 않고, 내가 쓴 장면 글만으로 그림을 만들어요.</p>
          ) : null}
          {canReset ? (
            <div className="cuts-reset-row">
              <button
                className="button-secondary"
                type="button"
                disabled={anyGenerating || Boolean(busy.reset)}
                onClick={() => setResetAsk(true)}
              >초기화</button>
              <span>{anyGenerating
                ? "그림을 만드는 중에는 초기화할 수 없어요."
                : "분위기를 다시 고르고, 만든 컷 그림을 모두 지워요."}</span>
            </div>
          ) : null}
          {localErrors.reset ? <InlineError>{localErrors.reset}</InlineError> : null}
        </section>
        {resetAsk ? (
          <ResetCutsDialog
            madeCount={madeCount}
            regenRemaining={data.image_regen_remaining}
            busy={Boolean(busy.reset)}
            onClose={() => setResetAsk(false)}
            onConfirm={() => void resetCuts()}
          />
        ) : null}
        {sampleKey ? (() => {
          const option = mvArtDirections.find((candidate) => candidate.key === sampleKey) ?? mvArtDirections[0];
          return (
            <StyleSampleDialog
              kicker="그림 분위기 예시"
              label={option.label}
              desc={option.desc}
              imageUrl={`/mv-styles/${option.key}.webp`}
              wide
              onClose={() => setSampleKey(null)}
              onConfirm={() => {
                setArtDirection(option.label);
                setSampleKey(null);
              }}
            />
          );
        })() : null}
        {/*
          🚨 「이 느낌으로 갈까요?」 확정 UI 가 **여기 있었다** — 컷 카드와 분리된 별도 띠로
             화면 위에 떠 있었다. 그래서 무엇을 확정하는 건지 눈으로 이어지지 않았고,
             「지금 확정해야 한다」가 인식되지 않았다(2026-08-13 jery).
          🔑 확정 대상은 **기준 컷 그림**이다. 그래서 그 카드 안으로 들여보냈다 —
             아래 `CutCard` 의 `confirmBase` 자리. 묻는 말과 그 답의 대상이 같은 상자 안에 있다.
        */}
        {cascade ? (
          <div className="cascade-offer" role="status">
            {cascade.stage === "offer" ? (
              <>
                <div>
                  <strong>업로드한 그림을 새 기준으로 삼을까요?</strong>
                  <p>
                    이미 만든 뒤 컷 {cascade.count}개를 새 그림에 맞춰 한 장씩 다시 그려요.
                    다시 만들기 횟수를 컷마다 1번씩 써요(남은 횟수 {data.image_regen_remaining}번).
                    {data.image_regen_remaining < cascade.count ? " 남은 횟수만큼만 다시 그릴 수 있어요." : ""}
                  </p>
                </div>
                <div className="cascade-offer-actions">
                  <button className="button-secondary" type="button" onClick={() => setCascade(null)}>그대로 두기</button>
                  <button className="button-primary" type="button" disabled={Boolean(busy["generate-all"])} onClick={() => void regenerateFromNewBase()}>다시 그리기</button>
                </div>
              </>
            ) : (
              <InlineSpinner label={batchProgress ? `${batchProgress.done + 1}/${batchProgress.total}번째 컷을 새 기준으로 다시 그리는 중...` : "다시 그리는 중..."} />
            )}
          </div>
        ) : null}
        {data.work.base_cut_confirmed && data.cuts.some((cut) => cut.idx > 1 && !cut.image_asset_id && !cut.current_generation_job_id) ? (
          <button
            className="button-primary generate-all-button"
            type="button"
            disabled={Boolean(busy["generate-all"])}
            onClick={() => void generateRemaining()}
          >
            {/* 🔑 순차로 바뀌면서 이 버튼은 «오래 걸리는 버튼» 이 됐다(6번). 진행 중이라는
                표시가 없으면 멈춘 것처럼 보인다 — 몇 번째를 만들고 있는지까지 적는다. */}
            {busy["generate-all"]
              ? <InlineSpinner label={batchProgress ? `${batchProgress.done + 1}/${batchProgress.total}번째 컷 만드는 중...` : "컷 만드는 중..."} />
              : "나머지 컷 만들기"}
          </button>
        ) : null}
        <section className="cut-grid" aria-label="뮤직비디오 컷">
          {data.cuts.map((cut) => (
            <CutCard
              key={`${cut.id}:${cut.current_generation_job_id ?? "idle"}:${cut.image_asset_id ?? "empty"}`}
              cut={cut}
              imageUrl={imageUrls[cut.id]}
              baseConfirmed={data.work.base_cut_confirmed}
              imageDisabled={!artDirection || Boolean(busy[`image:${cut.id}`])}
              uploadDisabled={Boolean(busy[`upload:${cut.id}`])}
              imageRegenRemaining={data.image_regen_remaining}
              otherCutBusy={generatingCutId !== null && generatingCutId !== cut.id}
              // 기준 컷 그림이 나왔는데 아직 확정 전이면, 그 카드 안에서 묻는다.
              awaitingBaseConfirm={Boolean(cut.is_base && cut.image_asset_id && !data.work.base_cut_confirmed)}
              confirmingBase={Boolean(busy["confirm-base"])}
              onConfirmBase={() => void confirmBase()}
              error={localErrors[`image:${cut.id}`] || localErrors[`upload:${cut.id}`]}
              onGenerate={(mode, instruction) => startImage(cut, mode, instruction).then(() => undefined)}
              onUpload={(event) => uploadImage(cut, event)}
              onRefresh={() => query.refresh().then(() => undefined)}
              onError={(message) => setLocalErrors((current) => ({ ...current, [`image:${cut.id}`]: message }))}
            />
          ))}
        </section>
        <p className="mv-next-stage-note">영상 만들기는 다음 단계에서 할 수 있어요.</p>
        {pageError ? <InlineError>{pageError}</InlineError> : null}
        <footer className="mv-footer">
          <button className="button-secondary" type="button" onClick={() => navigate(pathForStep(workId, "story"))}>이전</button>
          <button className="button-primary" type="button" disabled={!allComplete || !data.work.base_cut_confirmed} onClick={() => void finish()}>
            컷 그림 완성
          </button>
        </footer>
      </div>
    </StudioShell>
  );
}

function CutCard({
  cut,
  imageUrl,
  baseConfirmed,
  imageDisabled,
  uploadDisabled,
  imageRegenRemaining,
  otherCutBusy,
  awaitingBaseConfirm,
  confirmingBase,
  onConfirmBase,
  error,
  onGenerate,
  onUpload,
  onRefresh,
  onError,
}: {
  cut: Cut;
  imageUrl: string | undefined;
  baseConfirmed: boolean;
  imageDisabled: boolean;
  uploadDisabled: boolean;
  imageRegenRemaining: number;
  /** 같은 작품의 다른 컷이 지금 그림을 만들고 있다 — 그동안 이 컷은 시작할 수 없다. */
  otherCutBusy: boolean;
  awaitingBaseConfirm: boolean;
  confirmingBase: boolean;
  onConfirmBase: () => void;
  error: string | undefined;
  onGenerate: (mode: "generate" | "edit", instruction?: string) => Promise<void>;
  onUpload: (event: ChangeEvent<HTMLInputElement>) => Promise<void>;
  onRefresh: () => Promise<void>;
  onError: (message: string) => void;
}) {
  const jobId = cut.current_generation_job_id;
  const progress = useJobProgress(jobId);
  const [instruction, setInstruction] = useState("");
  const [editing, setEditing] = useState(false);
  const fileInput = useRef<HTMLInputElement | null>(null);
  const refreshRef = useRef(onRefresh);
  const errorRef = useRef(onError);
  const handledTerminal = useRef<string | null>(null);
  refreshRef.current = onRefresh;
  errorRef.current = onError;
  const active = Boolean(jobId && (!progress.progress || ["queued", "dispatched", "cancel_requested"].includes(progress.progress.state)));

  useEffect(() => {
    const state = progress.progress?.state;
    if (!state || ["queued", "dispatched", "cancel_requested"].includes(state)) return;
    if (handledTerminal.current === state) return;
    handledTerminal.current = state;
    if (state === "failed" || state === "outcome_unknown") {
      errorRef.current(imageFailureMessages[progress.progress?.error_code ?? "INTERNAL"]);
    }
    void refreshRef.current();
  }, [progress.progress?.error_code, progress.progress?.state]);

  const stop = async (): Promise<void> => {
    if (!jobId) return;
    await apiRequest(`/api/jobs/${encodeURIComponent(jobId)}/cancel`, {
      method: "POST",
      body: JSON.stringify({}),
    });
    await onRefresh();
  };

  const blockedByBase = cut.idx > 1 && !baseConfirmed;
  const regenExhausted = Boolean(imageUrl) && imageRegenRemaining <= 0;
  // 다른 컷이 도는 동안은 이 컷을 시작할 수 없다 — 서버(migration 016)도 같은 규칙으로 거절한다.
  const waitingForOtherCut = otherCutBusy && !active;
  const generationDisabled = imageDisabled || blockedByBase || regenExhausted || waitingForOtherCut;
  const disabledReasonId = `cut-${cut.id}-disabled-reason`;
  // 🔑 이유는 «한 자리에 하나» 만 띄운다 — 같은 id 를 가진 문단이 둘이면 화면 낭독기가 엉킨다.
  const disabledReason = regenExhausted
    ? "그림 다시 만들기 횟수를 다 썼어요. 내 그림 업로드는 계속 할 수 있어요."
    : waitingForOtherCut
      ? "다른 컷 그림을 만들고 있어요. 한 장씩 차례로 만들어요."
      : null;
  return (
    <article className={`cut-card${cut.is_base ? " base" : ""}${awaitingBaseConfirm ? " awaiting-confirm" : ""}`}>
      <header>
        <div>
          <b>컷 {cut.idx}</b>
          {cut.is_base ? <span>기준 컷</span> : null}
          {cut.image_from_upload ? <span className="cut-upload-badge">업로드한 그림</span> : null}
        </div>
      </header>
      <div className="cut-image-stage">
        {imageUrl ? <img src={imageUrl} alt={`컷 ${cut.idx}: ${cut.scene_text}`} /> : null}
        {/*
          🚨 종전엔 `!imageUrl && active` 였다 — «다시 만들기» 는 옛 그림이 아직 있는 상태라
             조건이 거짓이 되어 **아무 표시도 뜨지 않았다**(2026-08-14 jery 4번).
             누른 사람 눈에는 그림이 그대로라 「안 눌렸나」로 읽힌다.
          🔑 그림이 있으면 그 위에 덮어서 돈다 — 옛 그림을 지우지 않는 이유는, 지우면
             「내 그림이 사라졌다」가 되고 중지했을 때 돌아갈 자리도 없어지기 때문이다.
        */}
        {active ? (
          <div className={`cut-progress${imageUrl ? " over-image" : ""}`}>
            <span />{progress.progress?.state === "queued" ? "차례 기다리는 중" : "그림 만드는 중"}
          </div>
        ) : null}
        {!imageUrl && !active ? <div className="cut-placeholder" aria-hidden="true">{cut.idx}</div> : null}
      </div>
      <p className="cut-scene-text">{cut.scene_text}</p>
      {/* 확정은 «이 그림» 에 대한 물음이다 — 그림 바로 아래, 같은 상자 안에 둔다. */}
      {awaitingBaseConfirm ? (
        <div className="base-confirm-inline">
          <strong>이 느낌으로 갈까요?</strong>
          <p>확인하면 나머지 컷도 같은 등장인물과 분위기를 참고해요.</p>
          <button
            className="button-primary full-width"
            type="button"
            disabled={confirmingBase}
            onClick={onConfirmBase}
          >{confirmingBase ? <InlineSpinner label="확인 중..." /> : "이 느낌으로 계속"}</button>
        </div>
      ) : null}
      {blockedByBase ? <p className="cut-gate">기준 컷을 먼저 확인해 주세요.</p> : null}
      {disabledReason ? <p className="cut-gate" id={disabledReasonId}>{disabledReason}</p> : null}
      {error ? <p className="cut-error" role="alert"><strong>만들지 못했어요</strong>{error}<small>몰래 다른 방식으로 만들지 않고 알려드려요.</small></p> : null}
      {active ? (
        <button className="button-secondary full-width" type="button" onClick={() => void stop()}>중지</button>
      ) : (
        <div className="cut-actions">
          <button
            className={imageUrl ? "button-secondary" : "button-primary"}
            type="button"
            disabled={generationDisabled}
            aria-describedby={disabledReason ? disabledReasonId : undefined}
            onClick={() => void onGenerate("generate")}
          >{imageUrl ? "다시 만들기" : "그림 만들기"}</button>
          {imageUrl ? <button className="button-secondary" type="button" disabled={generationDisabled} aria-describedby={disabledReason ? disabledReasonId : undefined} onClick={() => setEditing((value) => !value)}>말로 고치기</button> : null}
          {/* 종전에는 올리는 동안 버튼이 «비활성» 이 될 뿐 아무 표시가 없었다 — 멈춘 것처럼 보인다. */}
          <button className="button-secondary" type="button" disabled={uploadDisabled} aria-label="내 그림 업로드 (PNG·JPG·WEBP, 10MB 이하)" onClick={() => fileInput.current?.click()}>
            {uploadDisabled ? <InlineSpinner label="올리는 중..." /> : "내 그림 업로드"}
          </button>
          <input ref={fileInput} className="sr-only" type="file" accept="image/png,image/jpeg,image/webp" onChange={(event) => void onUpload(event)} />
        </div>
      )}
      {editing ? (
        <div className="cut-edit-box">
          <label>어떻게 고칠까요?</label>
          <textarea value={instruction} maxLength={600} placeholder="예: 하늘을 보라색 노을로 바꿔 줘" onChange={(event) => setInstruction(event.target.value)} />
          <CharCounter value={instruction} max={600} />
          <button className="button-primary" type="button" disabled={!instruction.trim() || generationDisabled} aria-describedby={disabledReason ? disabledReasonId : undefined} onClick={() => void onGenerate("edit", instruction.trim())}>고치기</button>
        </div>
      ) : null}
    </article>
  );
}

/**
 * 「초기화」 확인 창 — 모양은 「수업에서 나갈까요?」 창(`exit-dialog`)과 같다.
 * 🔑 처음 초점은 «그대로 두기» 에 둔다. 지우는 쪽이 기본이면 Enter 한 번에 그림이 사라진다.
 */
function ResetCutsDialog({
  madeCount,
  regenRemaining,
  busy,
  onClose,
  onConfirm,
}: {
  madeCount: number;
  regenRemaining: number;
  busy: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const keepRef = useRef<HTMLButtonElement | null>(null);
  useBackClosable(true, onClose);
  useEffect(() => {
    keepRef.current?.focus();
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="exit-dialog-backdrop" onClick={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <div className="exit-dialog" role="dialog" aria-modal="true" aria-labelledby="cuts-reset-title" aria-describedby="cuts-reset-desc">
        <h2 id="cuts-reset-title">정말 지울까요?</h2>
        <p id="cuts-reset-desc">
          {madeCount > 0 ? `만든 컷 그림 ${madeCount}장이 화면에서 지워지고, ` : ""}
          그림 분위기를 다시 고를 수 있어요. 그림 다시 만들기 횟수는 돌아오지 않아요(남은 {regenRemaining}번).
        </p>
        <div className="exit-dialog-actions">
          <button ref={keepRef} className="button-secondary" type="button" disabled={busy} onClick={onClose}>그대로 두기</button>
          <button className="button-primary" type="button" disabled={busy} onClick={onConfirm}>
            {busy ? <InlineSpinner label="지우는 중..." /> : "모두 지우기"}
          </button>
        </div>
      </div>
    </div>
  );
}
